import {
    Container,
    Title,
    Stack, Text, Grid, Group, Button
} from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { gamesApi, wishlistApi } from '../services/api'
import { HorizontalGameScroller } from '../components/HorizontalGameScroller'
import { HorizontalGameScrollerSkeleton } from '../components/HorizontalGameScrollerSkeleton'
import { PriceDropsTable } from '../components/PriceDropsTable'
import { PriceDropsTableSkeleton } from '../components/PriceDropsTableSkeleton'
import { SEOHead } from '../components/SEOHead'
import { useI18n } from '@/hooks/useI18n'
import { useAuth } from '@/hooks/useAuth'
import { useBranding } from '@/hooks/useBranding'

export const HomePage: React.FC = () => {
    const { t, country } = useI18n()
    const { isAuthenticated } = useAuth()
    const { canonicalUrl } = useBranding()
    const [priceDropsPage, setPriceDropsPage] = useState(1)

    const { data: featuredGames, isLoading: featuredGamesLoading } = useQuery({
        queryKey: ['featured-games', country],
        queryFn: () => gamesApi.getFeaturedGames(country),
        select: (response) => response.data.data,
    })

    const { data: priceDrops, isLoading: priceDropsLoading } = useQuery({
        queryKey: ['price-drops', priceDropsPage, country],
        queryFn: () => gamesApi.getPriceDrops(priceDropsPage, 10, undefined, country),
        select: (response) => response.data,
    })

    // Fetch biggest price drops for the homepage
    const { data: biggestPriceDrops, isLoading: biggestPriceDropsLoading } = useQuery({
        queryKey: ['biggest-price-drops', country],
        queryFn: () => gamesApi.getBiggestPriceDrops(country),
        select: (response) => response.data.data,
    })

    // Fetch recently released games for the homepage
    const { data: recentGames, isLoading: recentGamesLoading } = useQuery({
        queryKey: ['recently-released-games', country],
        queryFn: () => gamesApi.getRecentlyReleasedGames(country),
        select: (response) => response.data.data,
    })

    // Fetch user's wishlist items when authenticated
    const { data: wishlistData, isLoading: wishlistLoading } = useQuery({
        queryKey: ['user-wishlist', country],
        queryFn: () => wishlistApi.getWishlist(country),
        enabled: isAuthenticated,
        select: (response) => response.data.data,
    })

    const featuredGamesSection = (
        <div>
            <Group justify="space-between" align="center" mb="md">
                <Title order={2} fw={300}>{t('home.mostPopular.title')}</Title>
                <Button
                    component={Link}
                    to="/games?sortBy=popularity&sortOrder=desc"
                    variant="light"
                    color="customGreen"
                    size="xs"
                    fw={500}
                    mt={4}
                >
                    {t('home.mostPopular.viewAll')}
                </Button>
            </Group>
            {featuredGamesLoading ? (
                <HorizontalGameScrollerSkeleton count={6} />
            ) : featuredGames && featuredGames.length > 0 ? (
                <HorizontalGameScroller
                    gamePlatforms={featuredGames.map(gamePlatform => ({
                        ...gamePlatform,
                        lowestPrice: gamePlatform.lowestPrice || undefined
                    }))}
                />
            ) : (
                <Text c="dimmed">{t('home.loading.featuredGames')}</Text>
            )}
        </div>
    )

    const biggestPriceDropsSection = (
        biggestPriceDrops && biggestPriceDrops.length > 0 ? (
            <div>
                <Group justify="space-between" align="center" mb="md">
                    <Title order={2} fw={300}>{t('home.biggestPriceDrops.title')}</Title>
                    <Button
                        component={Link}
                        to="/games?sortBy=priceDrop&sortOrder=desc"
                        variant="light"
                        color="customGreen"
                        size="xs"
                        fw={500}
                        mt={4}
                    >
                        {t('home.biggestPriceDrops.viewAll')}
                    </Button>
                </Group>
                {biggestPriceDropsLoading ? (
                    <HorizontalGameScrollerSkeleton count={6} />
                ) : biggestPriceDrops && biggestPriceDrops.length > 0 ? (
                    <HorizontalGameScroller
                        gamePlatforms={biggestPriceDrops}
                    />
                ) : (
                    <Text c="dimmed">{t('home.loading.biggestPriceDrops')}</Text>
                )}
            </div>
        ) : null
    )

    const recentGamesSection = (
        <div>
            <Group justify="space-between" align="center" mb="md">
                <Title order={2} fw={300}>{t('home.recentGames.title')}</Title>
                <Button
                    component={Link}
                    to="/games?sortBy=releaseDate&sortOrder=desc"
                    variant="light"
                    color="customGreen"
                    size="xs"
                    fw={500}
                    mt={4}
                >
                    {t('home.recentGames.viewAll')}
                </Button>
            </Group>
            {recentGamesLoading ? (
                <HorizontalGameScrollerSkeleton count={6} />
            ) : recentGames && recentGames.length > 0 ? (
                <HorizontalGameScroller
                    gamePlatforms={recentGames.map(recentGame => ({
                        ...recentGame,
                        lowestPrice: recentGame.lowestPrice || undefined
                    }))}
                />
            ) : (
                <Text c="dimmed">{t('home.loading.recentGames')}</Text>
            )}
        </div>
    )

    const wishlistSection = (
        wishlistData && wishlistData.filter(item => !item.isOwned).length > 0 ? (
            <div>
                <Group justify="space-between" align="center" mb="md">
                    <Title order={2} fw={300}>{t('wishlist.title')}</Title>
                    <Button
                        component={Link}
                        to="/wishlist"
                        variant="light"
                        color="customGreen"
                        size="xs"
                        fw={500}
                        mt={4}
                    >
                        {t('wishlist.viewAll')}
                    </Button>
                </Group>
                {wishlistLoading ? (
                    <HorizontalGameScrollerSkeleton count={6} />
                ) : wishlistData && wishlistData.filter(item => !item.isOwned).length > 0 ? (
                    <HorizontalGameScroller
                        gamePlatforms={wishlistData
                            .filter(wishlistItem => wishlistItem.gamePlatform && !wishlistItem.isOwned)
                            .map(wishlistItem => {
                                const gamePlatform = wishlistItem.gamePlatform!;
                                return {
                                    ...gamePlatform,
                                    lowestPrice: gamePlatform.prices && gamePlatform.prices.length > 0 ? {
                                        amount: gamePlatform.prices[0].amount,
                                        currency: gamePlatform.prices[0].currency,
                                        priceDrop: gamePlatform.prices[0].priceDrop,
                                        percentageChange: gamePlatform.prices[0].percentageChange,
                                        store: {
                                            id: gamePlatform.prices[0].store.id,
                                            name: gamePlatform.prices[0].store.name,
                                            country: gamePlatform.prices[0].store.country,
                                        },
                                    } : undefined
                                };
                            })}
                    />
                ) : (
                    <Text c="dimmed">{t('wishlist.empty')}</Text>
                )}
            </div>
        ) : null
    )

    const priceDropsSection = (
        <div>
            <Title mb={6} order={2} fw={300}>{t('home.priceDrops.title')}</Title>
            {priceDropsLoading ? (
                <PriceDropsTableSkeleton count={5} />
            ) : (
                <PriceDropsTable
                    data={priceDrops || null}
                    loading={priceDropsLoading}
                    onPageChange={setPriceDropsPage}
                />
            )}
        </div>
    )

    return (
        <>
            <SEOHead
                title={t('seo.home.title')}
                description={t('seo.home.description')}
                canonicalUrl={canonicalUrl}
                type="website"
            />
            <Container size="xl" py="xl">
                {/* Large screens: Side by side layout */}
                <Grid visibleFrom="lg">
                    <Grid.Col span={8}>
                        <Stack gap="xl">
                            {featuredGamesSection}
                            {biggestPriceDropsSection}
                            {recentGamesSection}
                            {isAuthenticated && wishlistSection}
                        </Stack>
                    </Grid.Col>
                    <Grid.Col span={4}>
                        {priceDropsSection}
                    </Grid.Col>
                </Grid>

                {/* Small screens: Stacked layout */}
                <Stack gap="xl" hiddenFrom="lg">
                    {featuredGamesSection}

                    {biggestPriceDropsSection}
                    {recentGamesSection}
                    {isAuthenticated && wishlistSection}
                    {priceDropsSection}
                </Stack>
            </Container>
        </>
    )
} 